import { Layout } from "@/components/layout/Layout";
import { Link } from "wouter";
import { ChevronRight } from "lucide-react";

export default function PrivacyPage() {
  return (
    <Layout>
      <div className="container py-8 md:py-12">
        {/* Breadcrumbs */}
        <div className="flex items-center text-sm text-muted-foreground mb-8">
          <Link href="/">
            <span className="hover:text-primary cursor-pointer">Home</span>
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span>Privacy Policy</span>
        </div>
        
        {/* Page Content */}
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          
          <div className="prose max-w-none">
            <p className="text-muted-foreground">Last updated: April 27, 2025</p>
            
            <h2>1. Introduction</h2>
            <p>
              At StayMiddle, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our services.
            </p>
            
            <h2>2. Information We Collect</h2>
            <p>
              We collect various types of information, including:
            </p>
            <ul>
              <li><strong>Personal Information:</strong> Name, email address, phone number, billing address, and payment details.</li>
              <li><strong>Account Information:</strong> Login credentials, account preferences, and user role (Property Manager, Agent, or Traveler).</li>
              <li><strong>Property Information:</strong> For Property Managers, details about listed properties, including address, features, availability, and pricing.</li>
              <li><strong>Transaction Information:</strong> Booking details, payment history, and commission records.</li>
              <li><strong>Usage Information:</strong> How you interact with our Platform, including pages visited, features used, and time spent.</li>
              <li><strong>Device Information:</strong> Device type, IP address, browser type, and operating system.</li>
            </ul>
            
            <h2>3. How We Collect Information</h2>
            <p>
              We collect information through various methods:
            </p>
            <ul>
              <li><strong>Direct Interactions:</strong> When you register, list a property, make a booking, or contact our support.</li>
              <li><strong>Automated Technologies:</strong> Through cookies, web beacons, and similar technologies.</li>
              <li><strong>Third Parties:</strong> Payment processors, identity verification services, and other partners.</li>
            </ul>
            
            <h2>4. How We Use Your Information</h2>
            <p>
              We use your information for various purposes, including:
            </p>
            <ul>
              <li>Providing and improving our services</li>
              <li>Processing transactions and commissions</li>
              <li>Facilitating communication between users</li>
              <li>Personalizing your experience</li>
              <li>Preventing fraud and ensuring security</li>
              <li>Complying with legal obligations</li>
              <li>Marketing our services (with your consent)</li>
            </ul>
            
            <h2>5. Information Sharing</h2>
            <p>
              We may share your information with:
            </p>
            <ul>
              <li><strong>Other Users:</strong> To facilitate bookings, we share necessary information between Property Managers, Agents, and Travelers.</li>
              <li><strong>Service Providers:</strong> Third parties that help us provide our services, such as payment processors and hosting providers.</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights.</li>
            </ul>
            
            <h2>6. Data Security</h2>
            <p>
              We implement appropriate security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
            
            <h2>7. Your Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal data, including:
            </p>
            <ul>
              <li>Right to access your data</li>
              <li>Right to correct inaccurate data</li>
              <li>Right to delete your data</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            
            <h2>8. Cookies</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage patterns, and deliver personalized content. For more information, please see our <Link href="/cookies"><span className="text-primary hover:underline">Cookie Policy</span></Link>.
            </p>
            
            <h2>9. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>
            
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information.
            </p>
            
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
            
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at <a href="mailto:privacy@staymiddle.com" className="text-primary hover:underline">privacy@staymiddle.com</a>.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}